import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// Pages
import LoginPage from './pages/LoginPage';
import DepartmentDashboardsPage from './pages/DepartmentDashboardsPage';
import AnalyticsReportsPage from './pages/AnalyticsReportsPage';
import AnalyticsDataExplorerPage from './pages/AnalyticsDataExplorerPage';
import HelpSupportPage from './pages/HelpSupportPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import ProjectsPage from './pages/ProjectsPage';
import UsersPage from './pages/UsersPage';
import NotFoundPage from './pages/NotFoundPage';
import AdminLayout from './pages/AdminLayout';
import ExcoLandingPage from './pages/ExcoLandingPage';
import ITAssetManagementPage from './pages/ITAssetManagementPage';
import ITAssetApprovalsPage from './pages/ITAssetApprovalsPage';

// Components
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';

// Utils
import { isAuthenticated, getUser, isAdmin } from './utils/auth';
import { getAppSettings, getLandingPath } from './utils/settings';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [, setUser] = useState(null);
  const [landingPath, setLandingPath] = useState(getLandingPath(getAppSettings().defaultLandingPage));

  useEffect(() => {
    if (isAuthenticated()) {
      setIsLoggedIn(true);
      const userData = getUser();
      setUser(userData);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const applySettings = () => {
      const settings = getAppSettings();
      setLandingPath(getLandingPath(settings.defaultLandingPage));
      document.body.classList.toggle('compact-tables', Boolean(settings.compactTables));
    };

    applySettings();
    globalThis.addEventListener('appSettingsChanged', applySettings);
    return () => globalThis.removeEventListener('appSettingsChanged', applySettings);
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <Router>
      {/* Skip-to-content link for keyboard / screen-reader users */}
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <Routes>
        {/* ==================== PUBLIC ROUTES ==================== */}
        <Route
          path="/login"
          element={<LoginPage setIsLoggedIn={setIsLoggedIn} setUser={setUser} />}
        />

        {/* ==================== PROTECTED ROUTES ==================== */}
        {isLoggedIn ? (
          <>
            {/* EXCO Landing Page — available to EXCO always, and USER/EDITOR with 2+ depts */}
            <Route
              path="/exco-home"
              element={<ExcoLandingPage setIsLoggedIn={setIsLoggedIn} setUser={setUser} />}
            />

            {/* Admin Routes */}
            {isAdmin() && (
              <Route
                path="/admin/*"
                element={
                  <div className="app-container">
                    <Navbar setIsLoggedIn={setIsLoggedIn} setUser={setUser} />
                    <div className="app-body">
                      <Sidebar />
                      <main id="main-content" className="app-content">
                        <AdminLayout />
                      </main>
                    </div>
                  </div>
                }
              />
            )}

            {/* Regular User Routes */}
            <Route
              path="/*"
              element={
                <div className="app-container">
                  <Navbar setIsLoggedIn={setIsLoggedIn} setUser={setUser} />
                  <div className="app-body">
                    <Sidebar />
                    <main id="main-content" className="app-content">
                      <Routes>
                        <Route path="/dashboard" element={<DepartmentDashboardsPage />} />
                        <Route
                          path="/projects"
                          element={isAdmin() ? <ProjectsPage /> : <Navigate to="/dashboard" replace />}
                        />
                        <Route path="/it-assets" element={<ITAssetManagementPage />} />
                        <Route path="/it-assets/approvals" element={<ITAssetApprovalsPage />} />
                        <Route path="/users" element={<UsersPage />} />
                        <Route
                          path="/analytics/reports"
                          element={isAdmin() ? <AnalyticsReportsPage /> : <Navigate to="/dashboard" replace />}
                        />
                        <Route
                          path="/analytics/charts"
                          element={<Navigate to={isAdmin() ? '/admin/dashboard' : '/dashboard'} replace />}
                        />
                        <Route
                          path="/analytics/data"
                          element={isAdmin() ? <AnalyticsDataExplorerPage /> : <Navigate to="/dashboard" replace />}
                        />
                        <Route path="/help" element={<HelpSupportPage />} />
                        <Route path="/profile" element={<ProfilePage />} />
                        <Route path="/settings" element={<SettingsPage />} />

                        {/* Redirect non-admin users trying to access admin routes */}
                        {!isAdmin() && (
                          <Route path="/admin/*" element={<Navigate to="/dashboard" replace />} />
                        )}

                        {/* Default redirect */}
                        <Route path="/" element={<Navigate to={landingPath} replace />} />
                        
                        {/* 404 Page */}
                        <Route path="*" element={<NotFoundPage />} />
                      </Routes>
                    </main>
                  </div>
                </div>
              }
            />
          </>
        ) : (
          // Not logged in - redirect to login
          <Route path="*" element={<Navigate to="/login" replace />} />
        )}
      </Routes>
    </Router>
  );
}

export default App;
